import { useState } from "react";
import {
  EXPENSE_TYPE,
  INCOME_TYPE,
  type Category,
} from "../../common/commonConstant";
import useDeviceModeFlg from "../../common/useDeviceModeFlg";
import Spinner from "../../common/Spinner";
import type { TrendPeriod, TrendSeries, TrendUnit } from "./bop020w040";

const UNIT_OPTIONS: Array<{ value: TrendUnit; label: string }> = [
  { value: "month", label: "月別" },
  { value: "week", label: "週別" },
  { value: "day", label: "日別" },
];

interface BOP020W040TemplateProps {
  unit: TrendUnit;
  fromMonth: string;
  toMonth: string;
  isLoading: boolean;
  showResults: boolean;
  periods: TrendPeriod[];
  series: TrendSeries[];
  visibleKeys: Set<string>;
  onUnitChange: (value: TrendUnit) => void;
  onFromMonthChange: (value: string) => void;
  onToMonthChange: (value: string) => void;
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onClear: () => void;
  onBack: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onToggleSeries: (key: string, visible: boolean) => void;
  onToggleGroup: (keys: string[], visible: boolean) => void;
  onHideAll: () => void;
}

const TOTAL_COLORS: Record<Category, string> = {
  [INCOME_TYPE]: "#10b981",
  [EXPENSE_TYPE]: "#ef4444",
};

// 系列数が多くても隣同士が似た色にならないよう、色相を黄金角ずつずらす
const getSeriesColor = (item: TrendSeries, index: number) =>
  item.kind === "total"
    ? TOTAL_COLORS[item.type]
    : `hsl(${Math.round((index * 137.5) % 360)}, 65%, 45%)`;

// 縦軸の上限を「1・2・5×10^n」の切りのよい値に丸める
const getNiceMax = (value: number) => {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 5, 10].find((n) => value <= n * magnitude) ?? 10;
  return step * magnitude;
};

const formatAxisAmount = (value: number) =>
  value >= 10000
    ? `${(value / 10000).toLocaleString()}万`
    : value.toLocaleString();

const BOP020W040Template = ({
  unit,
  fromMonth,
  toMonth,
  isLoading,
  showResults,
  periods,
  series,
  visibleKeys,
  onUnitChange,
  onFromMonthChange,
  onToMonthChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onClear,
  onBack,
  onSubmit,
  onToggleSeries,
  onToggleGroup,
  onHideAll,
}: BOP020W040TemplateProps) => {
  const deviceModeFlg = useDeviceModeFlg();
  // 金額一覧に表示する区間（未選択時は期間の最終区間）
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const colorMap = new Map(
    series.map((item, index) => [item.key, getSeriesColor(item, index)]),
  );
  const visibleSeries = series.filter((item) => visibleKeys.has(item.key));
  const activeIndex =
    selectedIndex !== null && selectedIndex < periods.length
      ? selectedIndex
      : periods.length - 1;

  const handleDateInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.showPicker?.();
  };

  const handleDateInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Tab") return;
    e.preventDefault();
  };

  const renderLineChart = () => {
    const width = deviceModeFlg ? 360 : 960;
    const height = deviceModeFlg ? 260 : 380;
    const padding = {
      top: 16,
      right: 16,
      bottom: 36,
      left: deviceModeFlg ? 44 : 64,
    };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;
    const maxValue = getNiceMax(
      Math.max(0, ...visibleSeries.flatMap((item) => item.values)),
    );
    const columnWidth = plotWidth / periods.length;
    const getX = (index: number) =>
      padding.left + columnWidth * index + columnWidth / 2;
    const getY = (value: number) =>
      padding.top + plotHeight - (value / maxValue) * plotHeight;
    const yTicks = [0, 1, 2, 3, 4].map((i) => (maxValue / 4) * i);
    const labelStep = Math.ceil(periods.length / (deviceModeFlg ? 6 : 12));
    // 日別は点数が多いため、点を小さくして線を読みやすくする
    const pointRadius = periods.length > 60 ? 1.5 : 2.5;

    return (
      <svg
        className="trend-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="収支推移の折れ線グラフ"
      >
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              className="trend-grid-line"
              x1={padding.left}
              x2={width - padding.right}
              y1={getY(tick)}
              y2={getY(tick)}
            />
            <text
              className="trend-axis-label"
              x={padding.left - 6}
              y={getY(tick)}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {formatAxisAmount(tick)}
            </text>
          </g>
        ))}
        {periods.map((period, index) =>
          index % labelStep === 0 || index === periods.length - 1 ? (
            <text
              key={period.key}
              className="trend-axis-label"
              x={getX(index)}
              y={height - padding.bottom + 18}
              textAnchor="middle"
            >
              {deviceModeFlg ? period.shortAxisLabel : period.axisLabel}
            </text>
          ) : null,
        )}
        <line
          className="trend-cursor-line"
          x1={getX(activeIndex)}
          x2={getX(activeIndex)}
          y1={padding.top}
          y2={padding.top + plotHeight}
        />
        {visibleSeries.map((item) => {
          const color = colorMap.get(item.key);
          const points = item.values
            .map((value, index) => `${getX(index)},${getY(value)}`)
            .join(" ");
          return (
            <g key={item.key}>
              <polyline
                className={`trend-line trend-line-${item.kind}`}
                points={points}
                stroke={color}
              />
              {item.values.map((value, index) => (
                <circle
                  key={index}
                  cx={getX(index)}
                  cy={getY(value)}
                  r={index === activeIndex ? 4.5 : pointRadius}
                  fill={color}
                />
              ))}
            </g>
          );
        })}
        {/* 区間ごとの透明な当たり判定。ホバー（PC）またはタップ（SP）でその区間の金額を下に表示する */}
        {periods.map((period, index) => (
          <rect
            key={period.key}
            className="trend-hit-area"
            x={padding.left + columnWidth * index}
            y={padding.top}
            width={columnWidth}
            height={plotHeight}
            onMouseEnter={() => setSelectedIndex(index)}
            onClick={() => setSelectedIndex(index)}
          />
        ))}
      </svg>
    );
  };

  const renderSeriesCheckbox = (item: TrendSeries) => (
    <label
      key={item.key}
      className={`series-option series-option-${item.kind}`}
    >
      <input
        type="checkbox"
        checked={visibleKeys.has(item.key)}
        onChange={(e) => onToggleSeries(item.key, e.target.checked)}
      />
      <span
        className={`series-swatch series-swatch-${item.kind}`}
        style={{ borderColor: colorMap.get(item.key) }}
      />
      <span className="series-label" title={item.label}>
        {item.label}
      </span>
    </label>
  );

  // 種類（大項目・小項目）単位の一括表示切替。全件表示中ならチェック済みにする
  const renderGroupCheckbox = (label: string, items: TrendSeries[]) => {
    const keys = items.map((item) => item.key);
    const allVisible =
      keys.length > 0 && keys.every((key) => visibleKeys.has(key));
    return (
      <label className="group-option">
        <input
          type="checkbox"
          checked={allVisible}
          disabled={keys.length === 0}
          onChange={(e) => onToggleGroup(keys, e.target.checked)}
        />
        {label}
      </label>
    );
  };

  const renderTypeSection = (type: Category) => {
    const typeSeries = series.filter((item) => item.type === type);
    const largeSeries = typeSeries.filter((item) => item.kind === "large");
    const smallSeries = typeSeries.filter((item) => item.kind === "small");
    return (
      <div className="series-section">
        <div className="series-section-header">
          <span className="series-section-title">{type}</span>
          <div className="group-options">
            {renderGroupCheckbox("大項目すべて", largeSeries)}
            {renderGroupCheckbox("小項目すべて", smallSeries)}
          </div>
        </div>
        {typeSeries
          .filter((item) => item.kind === "total")
          .map(renderSeriesCheckbox)}
        {largeSeries.length === 0 ? (
          <div className="series-empty">期間内のデータがありません</div>
        ) : (
          largeSeries.map((large) => (
            <div key={large.key} className="series-large-group">
              {renderSeriesCheckbox(large)}
              <div className="series-small-list">
                {smallSeries
                  .filter(
                    (small) => small.categoryLarge === large.categoryLarge,
                  )
                  .map(renderSeriesCheckbox)}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div
      className={`bop020w040-container ${
        deviceModeFlg ? "deviceMode-sp" : "deviceMode-pc"
      }`}
    >
      {isLoading && (
        <div className="bop020w040-loading-overlay">
          <Spinner label="検索中..." />
        </div>
      )}
      <div className="screen-title">収支推移画面</div>
      {/* 検索条件 */}
      <details className="bop020w040-search" open>
        <summary>検索条件</summary>
        <div className="bop020w040-search-content">
          <form id="trendSearchForm" onSubmit={onSubmit}>
            <div className="bop020w040-form-row">
              <span className="bop020w040-form-label">集計単位</span>
              <div
                className="unit-options"
                role="radiogroup"
                aria-label="集計単位"
              >
                {UNIT_OPTIONS.map((option) => (
                  <label key={option.value} className="unit-option">
                    <input
                      type="radio"
                      name="trendUnit"
                      checked={unit === option.value}
                      onChange={() => onUnitChange(option.value)}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="bop020w040-form-row">
              <label className="bop020w040-form-label required-label">
                期間
              </label>
              {/* 月別は年月、週別・日別は日付で期間を指定する */}
              {unit === "month" ? (
                <div className="period-inputs">
                  <input
                    type="month"
                    value={fromMonth}
                    aria-label="開始年月"
                    onChange={(e) => onFromMonthChange(e.target.value)}
                    onClick={handleDateInputClick}
                    onKeyDown={handleDateInputKeyDown}
                  />
                  <span className="period-separator">～</span>
                  <input
                    type="month"
                    value={toMonth}
                    aria-label="終了年月"
                    onChange={(e) => onToMonthChange(e.target.value)}
                    onClick={handleDateInputClick}
                    onKeyDown={handleDateInputKeyDown}
                  />
                </div>
              ) : (
                <div className="period-inputs">
                  <input
                    type="date"
                    value={fromDate}
                    aria-label="開始日"
                    onChange={(e) => onFromDateChange(e.target.value)}
                    onClick={handleDateInputClick}
                    onKeyDown={handleDateInputKeyDown}
                  />
                  <span className="period-separator">～</span>
                  <input
                    type="date"
                    value={toDate}
                    aria-label="終了日"
                    onChange={(e) => onToDateChange(e.target.value)}
                    onClick={handleDateInputClick}
                    onKeyDown={handleDateInputKeyDown}
                  />
                </div>
              )}
            </div>
            {unit === "week" && (
              <div className="period-note">
                ※週の途中の日付を指定した場合は、その日を含む週（月曜日～日曜日）全体を集計します
              </div>
            )}
          </form>
        </div>
      </details>
      <div className="bop020w040-action-panel">
        <div className="bop020w040-action-group">
          <button
            type="button"
            className="bop020w040-sub-button"
            onClick={onBack}
          >
            戻る
          </button>
          <button
            type="button"
            className="bop020w040-sub-button"
            onClick={onClear}
            disabled={isLoading}
          >
            クリア
          </button>
        </div>
        <button
          form="trendSearchForm"
          type="submit"
          className="bop020w040-search-button"
          disabled={isLoading}
        >
          検索
        </button>
      </div>
      {showResults && (
        <div className="trend-layout">
          <div className="trend-card trend-chart-card">
            <div className="trend-card-header">収支推移グラフ</div>
            {visibleSeries.length === 0 ? (
              <div className="trend-placeholder">
                表示する項目を選択してください。
              </div>
            ) : (
              <>
                {renderLineChart()}
                <div className="trend-detail">
                  <div className="trend-detail-title">
                    {periods[activeIndex].label} の金額
                  </div>
                  <ul className="trend-detail-list">
                    {visibleSeries.map((item) => (
                      <li key={item.key} className="trend-detail-item">
                        <span
                          className="series-swatch series-swatch-total"
                          style={{ borderColor: colorMap.get(item.key) }}
                        />
                        <span className="trend-detail-label">
                          {item.kind === "small"
                            ? `${item.categoryLarge} / ${item.label}`
                            : item.label}
                        </span>
                        <span className="trend-detail-amount">
                          {item.values[activeIndex].toLocaleString()} 円
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
          <div className="trend-card trend-series-card">
            <div className="trend-card-header">
              <span>表示項目</span>
              <button
                type="button"
                className="bop020w040-sub-button hide-all-button"
                onClick={onHideAll}
                disabled={visibleKeys.size === 0}
              >
                すべて非表示
              </button>
            </div>
            {renderTypeSection(INCOME_TYPE)}
            {renderTypeSection(EXPENSE_TYPE)}
          </div>
        </div>
      )}
    </div>
  );
};

export default BOP020W040Template;
